<template>
  <b-modal
    id="risk_form_modal"
    @show="onModalShow"
    @hide="$emit('close')"
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    :modal-class="'rfm-fullscreen'"
    :dialog-class="'rfm-dialog'"
    :content-class="'rfm-content'"
    body-class="rfm-body"
  >
    <div class="d-flex align-items-center justify-content-between">
      <h3 class="h4 text-capitalize mb-0 font-weight-bold">
        {{ tf("risk.title", "Evaluación de Riesgo") }}
      </h3>
      <div class="d-flex align-items-center gap-2">
        <b-button
          size="sm"
          variant="primary"
          class="reload-btn mr-2"
          :disabled="loading || saving"
          @click="reloadAll"
          title="Recargar"
        >
          <span v-if="!reloading" class="reload-icon">↺</span>
          <b-spinner v-else small style="width: 14px; height: 14px" />
        </b-button>
        <b-badge pill variant="light-primary">
          {{ tf("custom_forms.state", "Estado") }}:
          {{ labelForState((currentState || "").toLowerCase()) }}
        </b-badge>
      </div>
    </div>

    <div class="form-container">
      <div v-if="loading" class="text-center my-2">
        <b-spinner label="Loading" variant="primary" />
      </div>

      <div v-else class="rfm-grid">
        <div class="header-grid mb-3">
          <div class="info-card card">
            <div class="info-row">
              <span class="info-label text-body">
                {{ tf("risk.reference_number", "Número de Referencia") }}:
              </span>
              <span class="info-value text-body">{{ referenceNumber }}</span>
            </div>
            <div class="info-row">
              <span class="info-label text-body">
                {{ tc("unit.title", 1, "Instalación") }}:
              </span>
              <span class="info-value text-body">{{ unitNameDisplay }}</span>
            </div>
            <div class="info-row">
              <span class="info-label text-body">REMS:</span>
              <span class="info-value text-body">{{ rems }}</span>
            </div>
            <div class="info-row">
              <span class="info-label text-body">SIRH:</span>
              <span class="info-value text-body">{{ sirh }}</span>
            </div>
            <div
              v-if="associatedUnitsDisplayList.length"
              class="info-row info-row-associated"
            >
              <span class="info-label text-body">
                {{
                  tf("simulacrum.associated_units", "Instalaciones asociadas")
                }}:
              </span>
              <ul class="associated-units-list mb-0 pl-0">
                <li
                  v-for="(unitLabel, index) in associatedUnitsDisplayList"
                  :key="`${unitLabel}-${index}`"
                  class="associated-units-item text-body"
                >
                  {{ unitLabel }}
                </li>
              </ul>
            </div>
            <div v-if="templateNames" class="info-row">
              <span class="info-label text-body">
                {{ tf("risk.template.title", "Plantilla") }}:
              </span>
              <span class="info-value text-body">{{ templateNames }}</span>
            </div>
          </div>

          <div class="history-card card">
            <div class="history-header d-flex align-items-center justify-content-between">
              <strong>{{ tf("custom_forms.history", "Historial") }}</strong>
              <small class="text-muted">
                {{ sortedHistory.length }} {{ tf("custom_forms.entries", "entradas") }}
              </small>
            </div>

            <div class="history-table" v-if="sortedHistory.length">
              <div
                class="history-row history-row--head bg-transparent"
                :style="historyGridStyle"
              >
                <div class="col-state text-body">
                  {{ tf("custom_forms.state", "Estado") }}
                </div>
                <div class="col-user text-body">
                  {{ tf("custom_forms.user", "Usuario") }}
                </div>
                <div class="col-date text-body">
                  {{ tf("custom_forms.date", "Fecha") }}
                </div>
                <div v-if="hasHistoryComments" class="col-note text-body">
                  {{ tf("custom_forms.note", "Nota") }}
                </div>
              </div>

              <div
                v-for="statusRow in visibleHistory"
                :key="statusRow.id"
                class="history-row"
                :style="historyGridStyle"
              >
                <div class="col-state">
                  <b-badge pill :variant="badgeFor(statusRow.name)" class="history-badge">
                    {{ labelForState(statusRow.name) }}
                  </b-badge>
                </div>
                <div class="col-user">
                  <div class="history-user" :title="formatUser(statusRow)">
                    <div class="user-name text-body">
                      {{ getUserName(statusRow) || "—" }}
                    </div>
                    <div v-if="getUserEmail(statusRow)" class="user-email text-body">
                      {{ getUserEmail(statusRow) }}
                    </div>
                  </div>
                </div>
                <div class="col-date">
                  <small class="history-date text-body">{{
                    formatDate(statusRow.created_at)
                  }}</small>
                </div>
                <div v-if="hasHistoryComments" class="col-note">
                  <small class="text-muted" :title="getStatusComment(statusRow)">
                    {{ getStatusComment(statusRow) || "—" }}
                  </small>
                </div>
              </div>
            </div>

            <div v-else class="text-muted small py-1">
              {{ tf("custom_forms.empty", "Sin movimientos") }}
            </div>

            <div v-if="sortedHistory.length > limitHistory" class="text-right mt-1">
              <b-button
                variant="link"
                size="sm"
                class="p-0"
                @click="showAllHistory = !showAllHistory"
              >
                {{
                  showAllHistory
                    ? tf("custom_forms.show_less", "Ver menos")
                    : tf("custom_forms.show_all", "Ver todo el historial")
                }}
              </b-button>
            </div>
          </div>

          <div v-if="toc.length" class="index-card card mt-2">
            <div class="index-header d-flex align-items-center justify-content-between">
              <h3 class="h5 text-capitalize mb-0 font-weight-bold">
                {{ tf("risk.index", "Índice") }}
              </h3>
            </div>
            <ul class="toc-list">
              <li v-for="sec in toc" :key="sec.id">
                <a
                  href="#"
                  class="toc-link text-body"
                  @click.prevent="scrollToAnchor(sec.id)"
                >
                  {{ sec.label }}
                </a>
                <ul v-if="sec.children && sec.children.length" class="toc-sub">
                  <li v-for="sub in sec.children" :key="sub.id">
                    <a
                      href="#"
                      class="toc-link text-body"
                      @click.prevent="scrollToAnchor(sub.id)"
                    >
                      {{ sub.label }}
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>

        <div class="content-grid">
          <div class="main-stack">
            <!--
              @changed/@dirty/@input → runner changes trigger autosave modal
              @flagged-preview → captures nona from front end in real time
              @flagged-synced → forces reload confirmation in RsikFlaggedAnswersPart
            -->
            <build-risk-form
              v-if="readyToRenderForm"
              :key="runnerKey"
              ref="runner"
              :risk-evaluation-id="riskEvaluationId"
              :template-snapshot="templateSnapshot"
              :template-source-ids="templateSourceIds"
              :current-state="currentState"
              :is-reviewer="isReviewer"
              :can-edit="canEditForm"
              @submit="onAnswersSubmit"
              @toc="onToc"
              @changed="onChildDirty('runner:changed')"
              @dirty="onChildDirty('runner:dirty')"
              @input="onChildDirty('runner:input')"
              @flagged-preview="onFlaggedPreview"
              @flagged-synced="onFlaggedSynced"
            />

            <risk-threat-part
              v-if="readyToRenderForm"
              ref="threats"
              :risk-evaluation-id="riskEvaluationId"
              :defer-save="true"
              :allow-partial="true"
              :skip-weight-validation="true"
              :autosave-mode="true"
              :can-edit="canEditForm"
              @changed="onChildDirty('threats:changed')"
              @dirty="onChildDirty('threats:dirty')"
              @input="onChildDirty('threats:input')"
              @weights-changed="onChildDirty('threats:weights')"
              @answers-changed="onChildDirty('threats:answers')"
            />

            <!--
              :front-flagged - passes the preview to the front ed in order to show NONA inmmediatly
              null = is that we still not recieving data we are showing what we charge from the back
            -->
            <risk-flagged-answers-part
              v-if="readyToRenderForm"
              ref="flagged"
              :risk-evaluation-id="riskEvaluationId"
              :defer-save="true"
              :autosave-mode="true"
              :can-edit="canEditForm"
              :front-flagged="frontFlaggedPreview"
              @changed="onChildDirty('flagged:changed')"
              @dirty="onChildDirty('flagged:dirty')"
              @input="onChildDirty('flagged:input')"
              @followups-changed="onChildDirty('flagged:followups')"
            />

            <div class="rfm-anchor rfm-aid-sec-final"></div>
            <risk-evaluation-final-part
              v-if="readyToRenderForm"
              v-model="evaluationFinal"
              :disabled="saving"
              :can-edit="canEditForm"
              class="mt-3"
            />
          </div>
        </div>
      </div>

      <div v-if="saving" class="rfm-busy-overlay">
        <div class="rfm-busy-box">
          <b-spinner small label="Saving"></b-spinner>
          <span class="ml-2">{{ tf("common.saving", "Guardando…") }}</span>
        </div>
      </div>
    </div>

<template #modal-footer="{ close }">
  <div class="w-100 rfm-footer">
    <div class="d-flex flex-row align-items-stretch justify-content-between">

      <!-- IZQUIERDA: Acciones principales -->
      <div class="d-flex align-items-center flex-wrap">

        <!-- Guardar (solo si puede editar) -->
        <b-button
          v-if="showSaveButton"
          :disabled="isBusyGlobal"
          variant="primary"
          class="mr-2"
          @click="onClickSave"
        >
          {{ tf("actions.save", "Guardar") }}
        </b-button>

        <small v-if="debugBusy" class="text-muted mr-2">busy: {{ busyText }}</small>

        <!-- ─── BLOQUE REVIEWER: in_review && isReviewer ─── -->
        <template v-if="currentState === 'in_review' && isReviewer">
          <b-button
            v-if="showApproveButton"
            variant="success"
            class="mr-1"
            :disabled="isBusyGlobal"
            @click="updateEvaluationStatus('approved')"
          >
            {{ tf("actions.approve", "Aprobar") }}
          </b-button>
          <b-button
            v-if="showRejectButton"
            variant="danger"
            class="mr-2"
            :disabled="isBusyGlobal"
            @click="updateEvaluationStatus('rejected')"
          >
            {{ tf("actions.reject", "Rechazar") }}
          </b-button>

          <!-- Enviar a segundo nivel de revisión -->
          <div class="reviewer-inline mr-2">
            <v-select
              v-model="selectedReviewer"
              :options="userOptions"
              :reduce="(u) => u.value"
              :clearable="false"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :placeholder="tf('custom_forms.select_reviewer', 'Selecciona usuario revisor')"
              input-id="reviewer-reassign-inline"
              class="select-opens-up"
            />
          </div>
          <b-button
            variant="warning"
            class="mr-0"
            :disabled="!selectedReviewer || isBusyGlobal"
            @click="updateEvaluationStatus('in_review', selectedReviewer)"
          >
            {{ tf("custom_forms.send_to_review", "Enviar a revisión") }}
          </b-button>
        </template>

        <!-- REASIGNAR DESPUÉS DE RECHAZO -->
        <template v-else-if="canReassignAfterRejected">
          <div class="reviewer-inline mr-2">
            <v-select
              v-model="selectedReassign"
              :options="reassignableOptions"
              :reduce="(u) => u.value"
              :clearable="false"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :placeholder="tf('custom_forms.select_reassigner', 'Selecciona usuario para reasignar')"
              input-id="reassign-after-reject"
              class="select-opens-up"
            />
          </div>
          <b-button
            variant="warning"
            class="mr-2"
            :disabled="!selectedReassign || isBusyReassign"
            @click="updateEvaluationStatus('reassigned', selectedReassign)"
          >
            {{ tf("custom_forms.reassign", "Reasignar") }}
          </b-button>
        </template>

        <!-- PERMISO ESPECIAL: reasignar en in_progress/reassigned -->
        <template v-else-if="canSpecialReassign">
          <div class="reviewer-inline mr-2">
            <v-select
              v-model="selectedReassign"
              :options="reassignableOptions"
              :reduce="(u) => u.value"
              :clearable="false"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :placeholder="tf('custom_forms.select_reassigner', 'Selecciona usuario para reasignar')"
              input-id="special-reassign"
              class="select-opens-up"
            />
          </div>
          <b-button
            variant="warning"
            class="mr-2"
            :disabled="!selectedReassign || isBusyGlobal"
            @click="updateEvaluationStatus('reassigned', selectedReassign)"
          >
            {{ tf("custom_forms.reassign", "Reasignar") }}
          </b-button>
        </template>
      </div>

      <!-- CENTRO: Enviar a revisión (solo owner en in_progress/reassigned) -->
      <div
        v-if="canSendToReview"
        class="d-none d-md-flex align-items-center justify-content-center flex-grow-1 reviewer-center"
      >
        <div class="reviewer-inline mr-2">
          <v-select
            v-model="selectedReviewer"
            :options="userOptions"
            :reduce="(u) => u.value"
            :clearable="false"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :placeholder="tf('custom_forms.select_reviewer', 'Selecciona usuario revisor')"
            input-id="reviewer"
            class="select-opens-up"
          />
        </div>
        <b-button
          variant="warning"
          :disabled="!selectedReviewer || isBusyGlobal"
          @click="updateEvaluationStatus('in_review', selectedReviewer)"
        >
          {{ tf("custom_forms.send_to_review", "Enviar a revisión") }}
        </b-button>
      </div>

      <!-- DERECHA: Cerrar -->
      <div class="d-flex align-items-center">
        <b-button
          :disabled="loading || saving"
          variant="danger"
          class="mb-0"
          @click="close()"
        >
          {{ tf("custom_forms.close", "Cerrar") }}
        </b-button>
      </div>
    </div>

    <div
      v-if="canSendToReview"
      class="d-flex d-md-none flex-column w-100 mt-2"
    >
      <div class="reviewer-inline w-100 mb-1">
        <v-select
          v-model="selectedReviewer"
          :options="userOptions"
          :reduce="(u) => u.value"
          :clearable="false"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :placeholder="tf('custom_forms.select_reviewer', 'Selecciona usuario revisor')"
          input-id="reviewer-mobile"
          :append-to-body="true"
          class="select-opens-up"
        />
      </div>
      <b-button
        variant="warning"
        class="w-100"
        :disabled="!selectedReviewer || isBusyGlobal"
        @click="updateEvaluationStatus('in_review', selectedReviewer)"
      >
        {{ tf("custom_forms.send_to_review", "Enviar a revisión") }}
      </b-button>
    </div>
  </div>
</template>

    <!-- Recovery: answer stored without any template -->
    <b-modal
      id="risk_template_recovery_modal"
      :title="tf('risk.template.recovery_title', 'Selecciona una plantilla')"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      ok-only
      :ok-disabled="!selectedTemplateIds.length || assigningTemplate"
      :ok-title="
        assigningTemplate
          ? tf('loading', 'Cargando...')
          : tf('actions.confirm', 'Confirmar')
      "
      @ok.prevent="confirmTemplateRecovery"
    >
      <p class="mb-1">
        {{
          tf(
            "risk.template.recovery_help",
            "Este formulario no tiene plantilla seleccionada. Elige una para poder continuar."
          )
        }}
      </p>
      <v-select
        v-model="selectedTemplateIds"
        multiple
        :options="availableTemplates"
        :reduce="(template) => template.id"
        label="name"
        :disabled="assigningTemplate"
        :placeholder="tf('risk.template.title', 'Plantilla')"
      />
    </b-modal>
  </b-modal>
</template>

<script>
import { BModal, BSpinner, BButton, BBadge } from "bootstrap-vue";
import vSelect from "vue-select";
import axios from "@axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import BuildRiskForm from "@/components/BuildRiskForm";
import RiskThreatPart from "@/components/RiskThreatPart.vue";
import RiskFlaggedAnswersPart from "@/components/RiskFlaggedAnswersPart.vue";
import RiskEvaluationFinalPart from "@/components/RiskEvaluationFinalPart.vue";

export default {
  name: "ShowRiskFormModal",
  components: {
    BModal,
    BSpinner,
    BButton,
    BBadge,
    vSelect,
    BuildRiskForm,
    RiskThreatPart,
    RiskFlaggedAnswersPart,
    RiskEvaluationFinalPart,
  },
  props: {
    riskEvaluationId: { type: [Number, String], required: true },
    templateSnapshot: { type: Array, default: () => [] },
    templateSourceIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      // Recovery flow for answers stored without any template (legacy empty records)
      needsTemplateSelection: false,
      orphanAnswerId: null,
      availableTemplates: [],
      selectedTemplateIds: [],
      assigningTemplate: false,

      reloading: false,

      debugBusy: false,

      loading: false,
      saving: false,

      referenceNumber: "",
      unitNameDisplay: "",
      rems: "",
      sirh: "",
      currentUnitId: null,
      unitRelated: [],
      currentState: "in_progress",
      templateNames: "",

      readyToRenderForm: false,
      runnerKey: 0,

      didBootstrap: false,
      creating: false,

      statuses: [],
      showAllHistory: false,
      limitHistory: 6,

      selectedReviewer: null,
      userOptions: [],
      reassignableOptions: [],
      selectedReassign: null,

      toc: [],
      _tocForm: [],
      _tocThreats: null,
      _tocNona: null,
      _tocFinal: null,
      _tocObserver: null,

      lastStatusUserId: null,
      ownerUserId: null,

      evaluationFinal: {
        flagged_comments: "",
        reviewed_residual_risk: null,
        grade_review_residual_risk: null,
        grade_reviwed_residual_risk: null,
        executive_resume: "",
      },

      asEnabled: true,
      asDebounceMs: 3000,
      asMaxIntervalMs: 20000,
      _asDebounce: null,
      _asMax: null,
      _asBusy: false,
      _asQueued: false,
      _asBusyStartedAt: null,

      autosaveRequestTimeoutMs: 60000,

      approvePermNeeded: null,
      inherentLevel: null,

      // Live NO/NA preview from BuildRiskForm.
      // null, no preview received yet → RiskFlaggedAnswersPart shows server data only
      // [], preview received, no NO/NA items
      // [...], preview received, show merged data
      frontFlaggedPreview: null,
    };
  },
  computed: {
    isBusyReassign() {
      return !!(this.loading || this.saving);
    },

    busyBreakdown() {
      const runnerComponent = this.$refs?.runner;
      const threatsComponent = this.$refs?.threats;
      const flaggedComponent = this.$refs?.flagged;

      const state = String(this.currentState || "").toLowerCase();
      const childrenCanBlock = ![
        "approved",
        "rejected",
        "canceled",
        "cancelled",
      ].includes(state);

      const runnerBusy = childrenCanBlock
        ? typeof runnerComponent?.isBusy === "function"
          ? runnerComponent.isBusy()
          : !!runnerComponent?._asSaving
        : false;

      const threatsBusy = childrenCanBlock
        ? typeof threatsComponent?.isBusy === "function"
          ? threatsComponent.isBusy()
          : threatsComponent?.saving != null &&
            typeof threatsComponent.saving === "object"
          ? Object.keys(threatsComponent.saving).length > 0
          : !!threatsComponent?.saving
        : false;

      const flaggedBusy = childrenCanBlock
        ? typeof flaggedComponent?.isBusy === "function"
          ? flaggedComponent.isBusy()
          : (typeof flaggedComponent?.hasAnySaving === "function"
              ? flaggedComponent.hasAnySaving()
              : false) || !!flaggedComponent?.isHardLoading
        : false;

      const autosaveIsStale =
        this._asBusy &&
        this._asBusyStartedAt != null &&
        Date.now() - this._asBusyStartedAt > this.autosaveRequestTimeoutMs + 5000;

      const modalAutosaveBusy = this._asBusy && !autosaveIsStale;

      return {
        loading: !!this.loading,
        saving: !!this.saving,
        autosave: !!modalAutosaveBusy,
        runner: !!runnerBusy,
        threats: !!threatsBusy,
        flagged: !!flaggedBusy,
      };
    },

    busyText() {
      const b = this.busyBreakdown || {};
      const on = Object.keys(b).filter((k) => b[k]);
      return on.length ? on.join(",") : "idle";
    },

    isBusyGlobal() {
      const b = this.busyBreakdown || {};
      return Object.keys(b).some((k) => !!b[k]);
    },

    isReviewer() {
      try {
        if (!(this.$can && this.$can("review_risk_evaluation", "RiskEvaluation")))
          return false;
      } catch (_) {
        return false;
      }

      const state = String(this.currentState || "").toLowerCase();
      if (state !== "in_review") return false;

      let assignedId = this.lastStatusUserId;
      let assignedEmail = null;

      const list = Array.isArray(this.sortedHistory) ? this.sortedHistory : [];
      const lastReview = list.find(
        (statusRow) => String(statusRow?.name || "").toLowerCase() === "in_review"
      );

      if (lastReview) {
        assignedId =
          assignedId ||
          lastReview?.id_user ||
          lastReview?.user_id ||
          lastReview?.reviewer_id ||
          lastReview?.id_reviewer ||
          lastReview?.user?.id ||
          lastReview?.userId ||
          null;
        assignedEmail = lastReview?.user?.email || lastReview?.email || null;
      }

      const currentUserId = this.getLocalUserId();
      const currentUserEmail = this.getLocalUserEmail();

      const idMatch =
        Number(assignedId) > 0 &&
        Number(currentUserId) > 0 &&
        Number(assignedId) === Number(currentUserId);

      const emailMatch =
        assignedEmail &&
        currentUserEmail &&
        String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase();

      return !!(idMatch || emailMatch);
    },

    canEditForm() {
      const state = String(this.currentState || "").toLowerCase();
      const currentUserId = this.getLocalUserId();
      const currentUserEmail = this.getLocalUserEmail();

      if (!currentUserId && !currentUserEmail) return false;

      if (["approved", "rejected", "canceled", "cancelled"].includes(state)) return false;

      // if (state === "in_review") return !!this.isReviewer;

      if (state === "in_review") return false;

      if (state === "in_progress" || state === "reassigned") {
        const ownerId = this.ownerUserId ? Number(this.ownerUserId) : null;
        return !!(ownerId && currentUserId && Number(ownerId) === Number(currentUserId));
      }

      return false;
    },

    // Whoever currently holds the form (the user on the latest in_progress /
    // reassigned status) is the only one allowed to pick a missing template.
    // Same rule that governs the "send to review" button.
    canPickTemplate() {
      const state = String(this.currentState || "").toLowerCase();
      if (state !== "in_progress" && state !== "reassigned") return false;

      const currentUserId = this.getLocalUserId();
      const ownerId = this.ownerUserId ? Number(this.ownerUserId) : null;
      return !!(ownerId && currentUserId && Number(ownerId) === Number(currentUserId));
    },

    canSendToReview() {
      const state = String(this.currentState || "").toLowerCase();
      const currentUserId = this.getLocalUserId();
      const currentUserEmail = this.getLocalUserEmail();

      // Solo desde in_progress o reassigned
      if (state !== "in_progress" && state !== "reassigned") return false;

      if (!currentUserId && !currentUserEmail) return false;

      // Solo el owner puede enviar a revisión
      const ownerId = this.ownerUserId ? Number(this.ownerUserId) : null;
      return !!(ownerId && currentUserId && Number(ownerId) === Number(currentUserId));
    },

    canSpecialReassign() {
      try {
        const state = String(this.currentState || "").toLowerCase();
        //if (!this.canShowReassign) return false; // solo excluye approved/rejected
        if (state !== "in_progress" && state !== "reassigned" && state !== "in_review" && state !== "scheduled") return false; // Solo aplica en in_progress o reassigned
        return !!(this.$can && this.$can("special_reasing", "RiskEvaluation"));
      } catch (_) {
        return false;
      }
    },

    canReassignAfterRejected() {
      try {
        const state = String(this.currentState || "").toLowerCase();
        if (state !== "rejected") return false;
        return !!(
          this.$can("review_risk_evaluation", "RiskEvaluation") ||
          this.$can("approve_low_risk", "RiskEvaluation") ||
          this.$can("approve_high_risk", "RiskEvaluation")
        );
      } catch (_) {
        return false;
      }
    },

    //En teoría este ya no se ocuparía.
    canShowReassign() {
      const state = String(this.currentState || "").toLowerCase();
      return state !== "approved" && state !== "rejected";
    },

    sortedHistory() {
      const arrayCopy = Array.isArray(this.statuses) ? this.statuses.slice() : [];
      return arrayCopy.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    },

    visibleHistory() {
      const base = Array.isArray(this.sortedHistory) ? this.sortedHistory : [];
      return this.showAllHistory ? base : base.slice(0, this.limitHistory);
    },

    hasHistoryComments() {
      return (Array.isArray(this.statuses) ? this.statuses : []).some(
        (statusRow) => !!this.getStatusComment(statusRow)
      );
    },

    showSaveButton() {
      const state = String(this.currentState || "").toLowerCase();
      if (["approved", "rejected", "canceled", "cancelled"].includes(state)) return false;
      return this.canEditForm;
    },

    historyGridStyle() {
      const base = "max-content minmax(140px, 1fr) minmax(190px, max-content)";
      return {
        gridTemplateColumns: this.hasHistoryComments
          ? `${base} minmax(140px, 1fr)`
          : base,
      };
    },
    associatedUnitsDisplayList() {
      const sourceUnits = Array.isArray(this.unitRelated) ? this.unitRelated : [];
      const normalizedCurrentUnitId =
        Number.isFinite(Number(this.currentUnitId)) ? Number(this.currentUnitId) : null;
      const uniqueById = new Map();

      sourceUnits.forEach((unit) => {
        if (!unit || unit.id == null) return;
        const normalizedUnitId = Number(unit.id);
        if (normalizedCurrentUnitId != null && normalizedUnitId === normalizedCurrentUnitId) {
          return;
        }
        if (uniqueById.has(normalizedUnitId)) return;
        const unitName = unit.description != null && String(unit.description).trim() !== ""
          ? String(unit.description).trim()
          : `Unidad ${normalizedUnitId}`;
        const unitSirh = unit.sirh != null && String(unit.sirh).trim() !== ""
          ? String(unit.sirh).trim()
          : "No SIRH";
        uniqueById.set(normalizedUnitId, `${unitName} (${unitSirh})`);
      });

      return [...uniqueById.values()];
    },

    showApproveButton() {
      if (!(this.currentState === "in_review" && this.isReviewer)) return false;
      const requiredPermission = this.approvePermNeeded;

      try {
        if (!this.$can) return false;
        if (requiredPermission === "approve_low_risk")
          return !!this.$can("approve_low_risk", "RiskEvaluation");
        if (requiredPermission === "approve_high_risk")
          return !!this.$can("approve_high_risk", "RiskEvaluation");
        return !!(
          this.$can("approve_low_risk", "RiskEvaluation") ||
          this.$can("approve_high_risk", "RiskEvaluation")
        );
      } catch (_) {
        return false;
      }
    },

    showRejectButton() {
      if (!(this.currentState === "in_review" && this.isReviewer)) return false;
      try {
        return !!(this.$can && this.$can("review_risk_evaluation", "RiskEvaluation"));
      } catch (_) {
        return false;
      }
    },
  },
  created() {
    this.loadReviewerOptions().catch(() => {});
    this.loadReassignableOptions().catch(() => {});
  },
  beforeDestroy() {
    this.teardownTocObserver();
    this._clearAutosaveTimers();
  },
  watch: {
    evaluationFinal: {
      deep: true,
      handler() {
        this.scheduleAutosave("final:changed");
        this.computeApprovalPermission().catch(() => {});
      },
    },
    currentState() {
      this._clearStaleChildrenBusy();
    },
  },
  methods: {
    async reloadAll() {
      if (this.reloading || this.loading || this.saving) return;
      this.reloading = true;
      try {
        await this.onModalShow();
      } catch (_) {
      } finally {
        this.reloading = false;
      }
    },
    // ─── Flagged preview handlers

    // Called when BuildRiskForm emits 'flagged-preview' (on every model change).
    // Passes the frontend NO/NA list to RiskFlaggedAnswersPart for immediate display.
    onFlaggedPreview(payload) {
      try {
        const items = Array.isArray(payload?.items) ? payload.items : [];
        this.frontFlaggedPreview = items;
      } catch (_) {}
    },

    // Called when BuildRiskForm has synced flagged_answers to the backend.
    // Forces RiskFlaggedAnswersPart to reload so pending items become confirmed.
    onFlaggedSynced() {
      try {
        this.$refs.flagged?.forceRefresh?.({ mode: "soft" });
      } catch (_) {}
    },

    forceUnlockUi() {
      this.loading = false;
      this.saving = false;
      this._asBusy = false;
      this._asQueued = false;
      this._asBusyStartedAt = null;
      this._clearAutosaveTimers();

      // Reset flagged
      try {
        this.$refs?.flagged?.clearSaving?.();
      } catch (_) {}
      try {
        if (this.$refs?.flagged?.loadingMode !== undefined) {
          this.$refs.flagged.loadingMode = "idle";
        }
      } catch (_) {}

      // Reset runner  stays witrh _asSaving:true after flushPending failed
      try {
        const runner = this.$refs?.runner;
        if (runner) {
          if ("_asSaving" in runner) runner._asSaving = false;
          if ("saving" in runner) runner.saving = false;
          if ("loading" in runner) runner.loading = false;
        }
      } catch (_) {}

      // Reset threats
      try {
        const threats = this.$refs?.threats;
        if (threats) {
          if ("saving" in threats) threats.saving = false;
          if ("loading" in threats) threats.loading = false;
        }
      } catch (_) {}
    },

    _clearStaleChildrenBusy() {
      if (this.canEditForm) return;

      try {
        const threats = this.$refs?.threats;
        if (threats && typeof threats === "object" && "saving" in threats)
          threats.saving = false;
      } catch (_) {}

      try {
        const runner = this.$refs?.runner;
        if (runner && typeof runner === "object" && "_asSaving" in runner)
          runner._asSaving = false;
      } catch (_) {}

      try {
        const flagged = this.$refs?.flagged;
        flagged?.clearSaving?.();
      } catch (_) {}

      this._asBusy = false;
      this._asQueued = false;
      this._asBusyStartedAt = null;
      this._clearAutosaveTimers();
    },

    async waitForUiIdle({ timeoutMs = 65000 } = {}) {
      const startAt = Date.now();

      while (true) {
        const runnerComponent = this.$refs?.runner;
        const flaggedComponent = this.$refs?.flagged;

        const runnerBusy =
          typeof runnerComponent?.isBusy === "function"
            ? runnerComponent.isBusy()
            : !!runnerComponent?._asSaving;

        const flaggedBusy =
          typeof flaggedComponent?.isBusy === "function"
            ? flaggedComponent.isBusy()
            : (typeof flaggedComponent?.hasAnySaving === "function"
                ? flaggedComponent.hasAnySaving()
                : false) || !!flaggedComponent?.isHardLoading;

        const modalBusy = !!this._asBusy;

        if (!runnerBusy && !flaggedBusy && !modalBusy) return true;

        if (Date.now() - startAt > timeoutMs) {
          this.forceUnlockUi();
          this.$toast?.warning?.(
            this.tf(
              "common.timeout",
              "Se detectó una operación atascada. UI desbloqueada."
            )
          );
          return false;
        }

        await this._sleep(120);
      }
    },

    hasSpecialReasingForRisk() {
      try {
        return !!(this.$can && this.$can("special_reasing", "RiskEvaluation"));
      } catch (_) {
        return false;
      }
    },

    tf(key, fallback = "") {
      try {
        const out = this.$t ? this.$t(key) : key;
        return typeof out === "string" && out === key ? fallback : out;
      } catch {
        return fallback;
      }
    },

    tc(key, count = 1, fallback = "") {
      try {
        const out = this.$tc ? this.$tc(key, count) : key;
        return typeof out === "string" && out === key ? fallback : out;
      } catch {
        return fallback;
      }
    },

    humanize(str = "") {
      return String(str)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },

    labelForState(name) {
      const raw = String(name || "");
      const normalized = raw
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, "_");
      const aliases = { canceled: "cancelled" };
      const canonical = aliases[normalized] || normalized;

      const tryKeys = [
        `procedure_states.${canonical}`,
        `procedure_states.value.${canonical}`,
      ];
      for (const key of tryKeys) {
        const translated = this.$t ? this.$t(key) : key;
        if (typeof translated === "string" && translated !== key) return translated;
      }

      return this.humanize(canonical);
    },

    formatDate(iso) {
      if (!iso) return "";
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    },

    getUserName(statusRow) {
      return statusRow?.user_name || statusRow?.user?.name || "";
    },
    getUserEmail(statusRow) {
      const user = statusRow?.user;
      if (!user) return "";
      if (typeof user === "string") return user;
      return user?.email || "";
    },
    formatUser(statusRow) {
      const name = this.getUserName(statusRow);
      const email = this.getUserEmail(statusRow);
      return [name, email].filter(Boolean).join(" | ");
    },
    getStatusComment(statusRow) {
      return (
        statusRow?.comment ||
        statusRow?.comments ||
        statusRow?.note ||
        statusRow?.notes ||
        statusRow?.description ||
        ""
      );
    },

    getLocalUserId() {
      try {
        const raw =
          localStorage.getItem("user") || localStorage.getItem("userData") || "";
        if (!raw) return null;
        const obj = JSON.parse(raw);
        const candidates = [
          obj?.id,
          obj?.user?.id,
          obj?.data?.id,
          obj?.user_id,
          obj?.uid,
        ];
        for (const candidate of candidates) {
          const numberValue = Number(candidate);
          if (Number.isFinite(numberValue) && numberValue > 0) return numberValue;
        }
        return null;
      } catch {
        return null;
      }
    },

    getLocalUserEmail() {
      try {
        const raw =
          localStorage.getItem("user") || localStorage.getItem("userData") || "";
        if (!raw) return null;
        const obj = JSON.parse(raw);
        const candidates = [obj?.email, obj?.user?.email, obj?.data?.email];
        for (const candidate of candidates) {
          if (candidate && typeof candidate === "string" && candidate.includes("@"))
            return candidate;
        }
        return null;
      } catch {
        return null;
      }
    },

    badgeFor(name) {
      const map = {
        in_progress: "primary",
        in_review: "warning",
        approved: "success",
        rejected: "danger",
        scheduled: "info",
        canceled: "canceled",
        reassigned: "reassigned",
      };
      const key = String(name || "").toLowerCase();
      return map[key] || "secondary";
    },

    _extractReviewedResidualRiskValue(source = {}) {
      const raw =
        source.grade_review_residual_risk != null
          ? source.grade_review_residual_risk
          : source.grade_reviwed_residual_risk != null
          ? source.grade_reviwed_residual_risk
          : source.reviewed_residual_risk;

      const parsed = raw == null ? null : Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    },

    _normalizeFinalFromServer(evaluation = {}) {
      const reviewedResidualRisk = this._extractReviewedResidualRiskValue(evaluation);

      return {
        flagged_comments: evaluation.flagged_comments ?? "",
        reviewed_residual_risk: reviewedResidualRisk,
        grade_review_residual_risk: reviewedResidualRisk,
        grade_reviwed_residual_risk: reviewedResidualRisk,
        executive_resume: evaluation.executive_resume ?? "",
      };
    },

    _buildFinalSavePayload(finalModel = {}) {
      const reviewedResidualRisk = this._extractReviewedResidualRiskValue(finalModel);

      return {
        flagged_comments: finalModel.flagged_comments || null,
        executive_resume: finalModel.executive_resume || null,
        reviewed_residual_risk: reviewedResidualRisk,
        grade_review_residual_risk: reviewedResidualRisk,
        grade_reviwed_residual_risk: reviewedResidualRisk,
      };
    },

    async onModalShow() {
      // Reset stale busy state and preview on open
      this.forceUnlockUi();
      this._clearStaleChildrenBusy();
      this.frontFlaggedPreview = null; // reset: will be populated on first flagged-preview event

      try {
        this.readyToRenderForm = false;
        this.runnerKey += 1;

        await this.loadEvaluation();
        const answerReady = await this.ensureAnswerExists();
        await this.computeApprovalPermission();

        this.readyToRenderForm = answerReady !== false;

        this.$nextTick(() => {
          try {
            this.$refs.runner?.reloadForm?.();
          } catch (_) {}

          this.setupTocObserver();
          this._wireAutosaveChild(this.$refs.threats);
          this._wireAutosaveChild(this.$refs.flagged);
          this.scheduleAutosave("onModalShow");
        });
      } catch (_) {
        this.readyToRenderForm = true;
      }
    },

    buildUserOptionLabel(user) {
      const parts = [user.name, user.user, user.email].filter(Boolean);
      return parts.join(" | ");
    },

    async loadReviewerOptions() {
      try {
        const { data } = await axios.get("/v1/risk_evaluations/getReviewers", {
          timeout: this.autosaveRequestTimeoutMs,
        });
        const list = data?.data || [];
        this.userOptions = list.map((user) => ({
          value: user.id,
          label: this.buildUserOptionLabel(user),
        }));
      } catch {
        this.userOptions = [];
      }
    },

    async loadReassignableOptions() {
      try {
        const { data } = await axios.get("/v1/risk_evaluations/getReassignable", {
          timeout: this.autosaveRequestTimeoutMs,
        });
        const list = data?.data || [];
        this.reassignableOptions = list.map((user) => ({
          value: user.id,
          label: this.buildUserOptionLabel(user),
        }));
      } catch {
        this.reassignableOptions = [];
      }
    },

    async loadEvaluation() {
      if (!this.riskEvaluationId) return;

      this.loading = true;
      try {
        const { data } = await axios.get(
          `/v1/risk_evaluations/${this.riskEvaluationId}`,
          {
            timeout: this.autosaveRequestTimeoutMs,
          }
        );
        const evaluation = data && data.data ? data.data : data;

        this.referenceNumber = evaluation.reference_number || "";
        this.unitNameDisplay = evaluation.unit?.description || "";
        this.currentUnitId = evaluation.unit?.id ?? null;
        this.unitRelated = Array.isArray(evaluation.unit_related) ? evaluation.unit_related : [];
        this.rems = evaluation.rems ?? evaluation.unit?.rems ?? "";
        this.sirh = evaluation.sirh ?? evaluation.unit?.sirh ?? "";
        this.currentState = evaluation.last_status?.name || "in_progress";
        this.statuses = Array.isArray(evaluation.statuses) ? evaluation.statuses : [];

        this.lastStatusUserId =
          evaluation.last_status?.id_user ||
          evaluation.last_status?.user_id ||
          evaluation.last_status?.user?.id ||
          null;

        const stateLower = String(this.currentState || "").toLowerCase();
        let ownerId =
          evaluation.id_user || evaluation.user_id || evaluation.user?.id || null;

        const statusesList = Array.isArray(evaluation.statuses)
          ? evaluation.statuses
          : [];

        if (!ownerId && statusesList.length) {
          const reversed = statusesList.slice().reverse();
          const ownerStatus = reversed.find((statusRow) => {
            const rowState = String(statusRow?.name || "").toLowerCase();
            return rowState === "reassigned" || rowState === "in_progress";
          });
          if (ownerStatus) {
            ownerId =
              ownerStatus.id_user ||
              ownerStatus.user_id ||
              ownerStatus.reviewer_id ||
              ownerStatus.id_reviewer ||
              ownerStatus.user?.id ||
              ownerStatus.userId ||
              null;
          }
        }

        if ((stateLower === "in_progress" || stateLower === "reassigned")) {
          ownerId = this.lastStatusUserId || null;
        }

        this.ownerUserId = ownerId || null;

        if (stateLower === "rejected") {
          await this.loadReassignableOptions();
        } else {
          this.selectedReassign = null;
        }

        if (evaluation.formAnswer?.template_sources?.length) {
          this.templateNames = evaluation.formAnswer.template_sources
            .map((source) => (source && source.name ? source.name : null))
            .filter(Boolean)
            .join(", ");
        } else if (this.templateSourceIds?.length) {
          const sources = await this.resolveTemplateSourcesByIds(this.templateSourceIds);
          this.templateNames = sources.map((source) => source.name).join(", ");
        } else {
          this.templateNames = "";
        }

        this.evaluationFinal = this._normalizeFinalFromServer(evaluation);
      } finally {
        this.loading = false;
        this._clearStaleChildrenBusy();
      }
    },

    async resolveTemplateSourcesByIds(ids = []) {
      if (!Array.isArray(ids) || !ids.length) return [];
      try {
        const { data } = await axios.get("/v1/risk_templates", {
          timeout: this.autosaveRequestTimeoutMs,
        });
        const all = data?.data || [];
        const set = new Set(ids.map(Number));
        return all
          .filter((template) => set.has(Number(template.id)))
          .map((template) => ({ id: template.id, name: template.name }));
      } catch {
        return [];
      }
    },

    answerHasNoTemplate(answer) {
      if (!answer) return false;
      const hasSnapshot =
        Array.isArray(answer.template_snapshot) && answer.template_snapshot.length > 0;
      const hasSources =
        Array.isArray(answer.template_sources) && answer.template_sources.length > 0;
      const hasTemplate = !!(answer.template && answer.template.id);
      return !hasSnapshot && !hasSources && !hasTemplate;
    },

    deepClone(value) {
      return JSON.parse(JSON.stringify(value));
    },

    namespaceFieldIds(templateId, fields) {
      return (Array.isArray(fields) ? fields : []).map((field) => {
        const normalizedField = this.deepClone(field);
        normalizedField.item_id = `${templateId}:${normalizedField.item_id}`;
        if (Array.isArray(normalizedField.show_if)) {
          normalizedField.show_if = normalizedField.show_if.map((condition) =>
            condition && condition.field
              ? { ...condition, field: `${templateId}:${condition.field}` }
              : condition
          );
        }
        return normalizedField;
      });
    },

    buildSnapshotFromTemplates(templates) {
      const snapshot = [];
      (templates || []).forEach((template) => {
        this.namespaceFieldIds(template.id, template.data).forEach((field) =>
          snapshot.push(field)
        );
      });
      return snapshot;
    },

    async openTemplateRecovery() {
      try {
        const { data } = await axios.get("/v1/risk_templates", {
          timeout: this.autosaveRequestTimeoutMs,
        });
        this.availableTemplates = Array.isArray(data?.data) ? data.data : [];
      } catch (_) {
        this.availableTemplates = [];
      }

      if (!this.availableTemplates.length) {
        this.$toast?.error?.(
          this.tf(
            "risk.template.none_available",
            "No hay plantillas disponibles para asignar a este formulario."
          )
        );
        this.needsTemplateSelection = false;
        this.$root.$emit("bv::hide::modal", "risk_form_modal");
        return;
      }

      this.selectedTemplateIds = [];
      this.needsTemplateSelection = true;
      await this.$nextTick();
      this.$root.$emit("bv::show::modal", "risk_template_recovery_modal");
    },

    async confirmTemplateRecovery() {
      const chosen = (this.availableTemplates || []).filter((template) =>
        this.selectedTemplateIds.includes(template.id)
      );
      if (!chosen.length || !this.orphanAnswerId) return;

      const snapshot = this.buildSnapshotFromTemplates(chosen);
      if (!snapshot.length) {
        this.$toast?.error?.(
          this.tf(
            "risk.template.empty_selection",
            "Las plantillas seleccionadas no tienen preguntas."
          )
        );
        return;
      }

      this.assigningTemplate = true;
      try {
        const payload = new FormData();
        payload.append("template_snapshot", JSON.stringify(snapshot));
        payload.append(
          "template_sources",
          JSON.stringify(
            chosen.map((template) => ({
              id: template.id,
              name: template.name,
              id_tenant: template.id_tenant,
            }))
          )
        );

        await axios.post(`/v1/risk_form_answers/${this.orphanAnswerId}`, payload, {
          timeout: this.autosaveRequestTimeoutMs,
          headers: {
            "Content-Type": "multipart/form-data",
            "X-HTTP-Method-Override": "PUT",
          },
        });

        this.templateNames = chosen.map((template) => template.name).join(", ");
        this.needsTemplateSelection = false;
        this.$root.$emit("bv::hide::modal", "risk_template_recovery_modal");

        this.runnerKey += 1;
        await this.loadEvaluation();
        this.readyToRenderForm = true;
        this.$nextTick(() => {
          try {
            this.$refs.runner?.reloadForm?.();
          } catch (_) {}
        });
      } catch (assignError) {
        this.$toast?.error?.(
          assignError?.response?.data?.message ||
            this.tf(
              "risk.template.assign_failed",
              "No se pudo asignar la plantilla al formulario"
            )
        );
      } finally {
        this.assigningTemplate = false;
      }
    },

    async ensureAnswerExists() {
      if (this.didBootstrap || this.creating) return true;

      try {
        const { data } = await axios.get(
          `/v1/risk_form_answers/get-form-by-evaluation/${this.riskEvaluationId}`,
          {
            timeout: this.autosaveRequestTimeoutMs,
          }
        );

        const answer = (data && data.data) || data || null;
        if (this.answerHasNoTemplate(answer)) {
          this.orphanAnswerId = answer?.id ?? null;
          if (!this.canPickTemplate) {
            this.$toast?.error?.(
              this.tf(
                "risk.template.recovery_not_allowed",
                "Este formulario no tiene plantilla. El responsable de responderlo debe seleccionarla."
              )
            );
            return false;
          }
          await this.openTemplateRecovery();
          return false;
        }

        this.didBootstrap = true;
        return true;
      } catch (lookupError) {
        if (lookupError?.response?.status !== 404) {
          this.$toast?.error?.(
            lookupError?.response?.data?.message ||
              this.tf("custom_forms.cannot_load_form", "No se pudo cargar el formulario")
          );
          return false;
        }

        if (!this.templateSnapshot?.length && !this.templateSourceIds?.length) {
          if (this.canPickTemplate) {
            this.$emit("need-form-selection", {
              id: this.riskEvaluationId,
              referenceNumber: this.referenceNumber,
              unitId: this.currentUnitId,
              unit: {
                id: this.currentUnitId,
                description: this.unitNameDisplay,
                rems: this.rems,
                sirh: this.sirh,
              },
              lastStatusName: this.currentState,
            });
            return false;
          }
          this.$toast?.error?.(
            this.tf(
              "custom_forms.must_use_wizard",
              "Esta evaluación aún no tiene formulario. Iníciala desde el asistente."
            )
          );
          return false;
        }

        this.creating = true;
        try {
          const requestFormData = new FormData();
          requestFormData.append("id_risk_evaluation", this.riskEvaluationId);
          requestFormData.append("data", JSON.stringify({}));
          requestFormData.append(
            "template_snapshot",
            JSON.stringify(this.templateSnapshot || [])
          );

          let sourcesToSend = [];
          if (this.templateSourceIds && this.templateSourceIds.length) {
            sourcesToSend = await this.resolveTemplateSourcesByIds(
              this.templateSourceIds
            );
          }
          requestFormData.append("template_sources", JSON.stringify(sourcesToSend));

          await axios.post(`v1/risk_form_answers`, requestFormData, {
            timeout: this.autosaveRequestTimeoutMs,
            headers: { "Content-Type": "multipart/form-data" },
          });

          this.templateNames = sourcesToSend.length
            ? sourcesToSend.map((source) => source.name).join(", ")
            : this.templateSnapshot?.length
            ? this.tf("risk.template.composed", "Composed")
            : "";

          this.didBootstrap = true;
          await this.loadEvaluation();
          return true;
        } catch (createError) {
          this.$toast?.error?.(
            createError?.response?.data?.message ||
              this.tf("custom_forms.cannot_start_form", "No se pudo iniciar el formulario")
          );
          return false;
        } finally {
          this.creating = false;
        }
      }
    },

    onChildDirty(reason = "child") {
      this.scheduleAutosave(reason);
    },

    scheduleAutosave(reason = "unknown") {
      if (!this.canEditForm) return;
      if (!this.asEnabled || this.loading || this.saving) return;

      clearTimeout(this._asDebounce);
      this._asDebounce = setTimeout(
        () => this._doAutosave(`debounce:${reason}`),
        this.asDebounceMs
      );

      if (!this._asMax) {
        this._asMax = setTimeout(
          () => this._doAutosave(`max:${reason}`),
          this.asMaxIntervalMs
        );
      }
    },

    _clearAutosaveTimers() {
      clearTimeout(this._asDebounce);
      this._asDebounce = null;
      clearTimeout(this._asMax);
      this._asMax = null;
    },

    async _doAutosave() {
      if (!this.canEditForm) return;

      if (!this.asEnabled || this._asBusy || this.loading) {
        this._asQueued = true;
        return;
      }

      this._asBusy = true;
      this._asBusyStartedAt = Date.now();

      try {
        await this._saveThreatsAllowPartial(true);
        await this._saveFormSilent();
        await this._sleep(250);

        try {
          await this.$refs.flagged?.refreshIfChanged?.({ mode: "soft" });
        } catch (_) {}

        if (this.$refs?.flagged?.saveAll) {
          try {
            await this._withPartialSave(async () => {
              await this.$refs.flagged.saveAll();
            }, "flagged");
          } catch (_) {}
        }

        await this._saveFinalSilent({ timeoutMs: this.autosaveRequestTimeoutMs });
      } catch (_) {
      } finally {
        this._asBusy = false;
        this._asBusyStartedAt = null;
        this._clearAutosaveTimers();

        //cleans residual state of runner after autosave
        try {
          const runner = this.$refs?.runner;
          if (runner && typeof runner === "object") {
            if ("_asSaving" in runner) runner._asSaving = false;
            if ("saving" in runner) runner.saving = false;
          }
        } catch (_) {}

        if (this._asQueued) {
          this._asQueued = false;
          this.scheduleAutosave("queued");
        }
      }
    },

    async _withPartialSave(fn, scope = "both") {
      const isThreats = scope === "threats" || scope === "both";
      const isFlagged = scope === "flagged" || scope === "both";
      const isForm = scope === "form";

      const rxThreats =
        /(risk[_-]?evaluation[_-]?threat|risk[_-]?threat|\/threats?\/|\/threats$|\/weights|\/summary)/i;
      const rxFlagged = /(flagged[_-]?answers?|\/flagged_answers?)/i;
      const rxForm =
        /(risk[_-]?form[_-]?answers?|\/risk_form_answers|\/dynamic[_-]?forms?|\/custom[_-]?forms?|\/form_answers?)/i;

      const requestInterceptorId = axios.interceptors.request.use((config) => {
        try {
          const url = String(config?.url || "");
          const matchThreats = isThreats && rxThreats.test(url);
          const matchFlagged = isFlagged && rxFlagged.test(url);
          const matchForm = isForm && rxForm.test(url);

          if (matchThreats || matchFlagged || matchForm) {
            config.params = { ...(config.params || {}), partial: 1 };
            config.headers = {
              ...(config.headers || {}),
              "X-Requested-From": "autosave",
              ...(matchThreats ? { "X-Skip-Weights-Validation": "1" } : {}),
              ...(matchFlagged ? { "X-Skip-Empty-Validation": "1" } : {}),
              ...(matchForm ? { "X-Skip-Required-Validation": "1" } : {}),
            };

            if (!config.timeout) config.timeout = this.autosaveRequestTimeoutMs;
          }
        } catch (_) {}
        return config;
      });

      try {
        return await fn();
      } finally {
        axios.interceptors.request.eject(requestInterceptorId);
      }
    },

    async _saveThreatsAllowPartial(silent = false) {
      const threatsComponent = this.$refs?.threats;
      if (!threatsComponent) return;

      await this._withPartialSave(async () => {
        let didSpecificSave = false;

        try {
          if (typeof threatsComponent.saveWeights === "function") {
            await threatsComponent.saveWeights({ allowPartial: true, silent: !!silent });
            didSpecificSave = true;
          }
        } catch (_) {}

        try {
          if (typeof threatsComponent.saveAnswersOnly === "function") {
            await threatsComponent.saveAnswersOnly({
              allowPartial: true,
              skipWeightValidation: true,
              skipEmptyValidation: true,
              silent: !!silent,
            });
            didSpecificSave = true;
          } else if (typeof threatsComponent.saveSectionsOnly === "function") {
            await threatsComponent.saveSectionsOnly({
              allowPartial: true,
              skipWeightValidation: true,
              skipEmptyValidation: true,
              silent: !!silent,
            });
            didSpecificSave = true;
          }
        } catch (_) {}

        if (!didSpecificSave && typeof threatsComponent.saveAll === "function") {
          try {
            await threatsComponent.saveAll();
          } catch (_) {}
        }

        try {
          if (typeof threatsComponent.saveSummary === "function") {
            await threatsComponent.saveSummary({ allowPartial: true, silent: !!silent });
          }
        } catch (_) {}
      }, "threats");
    },

    async _saveFormSilent() {
      const runnerComponent = this.$refs?.runner;
      if (!runnerComponent) return;

      await this._withPartialSave(async () => {
        if (typeof runnerComponent.flushPending === "function") {
          await runnerComponent.flushPending({ timeoutMs: 120000 });
        }

        if (typeof runnerComponent.saveDraft === "function") {
          await runnerComponent.saveDraft({ silent: true });
          return;
        }

        if (typeof runnerComponent.submitForm === "function") {
          runnerComponent.submitForm();
          await this._sleep(200);
        } else if (runnerComponent.$refs?.form?.submit) {
          runnerComponent.$refs.form.submit();
          await this._sleep(200);
        }
      }, "form");
    },

    async _saveFinalSilent({ timeoutMs = null } = {}) {
      if (!this.riskEvaluationId) return;

      try {
        const payload = this._buildFinalSavePayload(this.evaluationFinal);
        const config = timeoutMs ? { timeout: timeoutMs } : undefined;
        await axios.put(`/v1/risk_evaluations/${this.riskEvaluationId}`, payload, config);
      } catch (_) {}
    },

    _normalizeValidationResult(res, fallbackMsg = "Validación fallida") {
      if (typeof res === "boolean") return { ok: !!res, message: res ? "" : fallbackMsg };

      if (res && typeof res === "object") {
        const ok = [res.ok, res.valid, res.success].find((v) => typeof v === "boolean");
        const msg =
          res.message ||
          res.error ||
          (Array.isArray(res.errors) ? res.errors.join(", ") : "");
        return { ok: ok === undefined ? true : ok, message: msg || fallbackMsg };
      }

      return { ok: true, message: "" };
    },

    async updateEvaluationStatus(newStatus, toUserId = null) {
      if (!this.riskEvaluationId) return;

      const statusLower = String(newStatus || "").toLowerCase();
      let shouldProceed = true;

      if (statusLower === "in_review") {
        if (!toUserId) {
          this.$toast?.error?.(
            this.tf("custom_forms.select_reviewer", "Selecciona usuario revisor")
          );
          return;
        }

        const uiIdle = await this.waitForUiIdle({
          timeoutMs: this.autosaveRequestTimeoutMs + 5000,
        });
        if (!uiIdle) return;

        const previousAutosaveEnabled = this.asEnabled;
        this.asEnabled = false;
        this._clearAutosaveTimers();
        this.saving = true;

        try {
          const runnerComponent = this.$refs?.runner;
          const threatsComponent = this.$refs?.threats;
          const flaggedComponent = this.$refs?.flagged;

          if (runnerComponent?.flushPending) {
            await runnerComponent.flushPending({ timeoutMs: 120000 });
          }

          if (runnerComponent?.validateBeforeReview) {
            const validation = await runnerComponent.validateBeforeReview();
            if (!validation || !validation.ok) {
              const missingFields = Array.isArray(validation?.missing)
                ? validation.missing
                : [];
              const missingSections = [
                ...new Set(missingFields.map((field) => field.section).filter(Boolean)),
              ];
              let message = this.tf(
                "risk.form_required_missing",
                "Faltan respuestas requeridas en el formulario"
              );
              if (missingSections.length) {
                message += `: ${missingSections.slice(0, 3).join(", ")}${
                  missingSections.length > 3 ? "…" : ""
                }`;
              }
              this.$toast?.error?.(message);
              shouldProceed = false;
              this.forceUnlockUi();
              return;
            }
          }

          if (threatsComponent?.validateBeforeReview) {
            const threatsValidation = this._normalizeValidationResult(
              await threatsComponent.validateBeforeReview(),
              this.tf("risk.threats_missing", "Faltan pesos o respuestas en Amenazas")
            );
            if (!threatsValidation.ok) {
              this.$toast?.error?.(threatsValidation.message);
              shouldProceed = false;
              this.forceUnlockUi();
              return;
            }
          } else if (threatsComponent?.validateBeforeSave) {
            const threatsValidation = this._normalizeValidationResult(
              await threatsComponent.validateBeforeSave(),
              this.tf("risk.threats_missing", "Faltan pesos o respuestas en Amenazas")
            );
            if (!threatsValidation.ok) {
              this.$toast?.error?.(threatsValidation.message);
              shouldProceed = false;
              this.forceUnlockUi();
              return;
            }
          }

          await this._saveThreatsAllowPartial(false);
          await this._saveFormSilent();

          try {
            if (runnerComponent?.syncFlaggedAnswersNow) {
              await runnerComponent.syncFlaggedAnswersNow();
            }
          } catch (_) {}

          if (flaggedComponent?.refreshIfChanged) {
            await flaggedComponent.refreshIfChanged({ mode: "hard" });
          } else if (flaggedComponent?.loadAll) {
            await flaggedComponent.loadAll({
              mode: "hard",
              preserveOpen: true,
              preserveDirty: true,
            });
          }

          if (flaggedComponent?.saveAll) {
            await flaggedComponent.saveAll();
          }

          await this._saveFinalSilent();
        } finally {
          this.saving = false;
          this.asEnabled = previousAutosaveEnabled;
        }
      }

      //if one validation fails we dont continue with the change of the status
      if (!shouldProceed) return;

      const guarded = ["approved", "rejected"];
      if (guarded.includes(String(newStatus)) && !this.isReviewer) return;

      try {
        this.loading = true;

        const payload = { status: newStatus };
        if (toUserId) payload.id_user = toUserId;

        await axios.post(
          `/v1/risk_evaluations/update_status/${this.riskEvaluationId}`,
          payload,
          {
            timeout: this.autosaveRequestTimeoutMs,
          }
        );

        this.currentState = newStatus;

        this.$toast?.success?.(
          `${this.tf("custom_forms.state", "Estado")}: ${this.labelForState(
            String(newStatus || "").toLowerCase()
          )}`
        );

        await this.loadEvaluation();
        await this.ensureAnswerExists();
        this.$nextTick(() => this.$refs.runner?.reloadForm?.());
      } catch (_) {
        this.$toast?.error?.(
          this.tf("custom_forms.update_state_error", "No se pudo actualizar el estado")
        );
      } finally {
        this.loading = false;
        this._clearStaleChildrenBusy();
      }
    },

    async onAnswersSubmit() {
      try {
        await this.$refs.runner?.reloadForm?.();
      } catch (_) {}
    },

    async onClickSave() {
      if (!this.riskEvaluationId) return;

      const uiIdle = await this.waitForUiIdle({
        timeoutMs: this.autosaveRequestTimeoutMs + 5000,
      });
      if (!uiIdle) return;

      try {
        this.saving = true;

        await this._saveThreatsAllowPartial(false);
        await this._saveFormSilent();
        await this._sleep(250);

        try {
          await this.$refs.flagged?.refreshIfChanged?.({ mode: "soft" });
        } catch (_) {}

        if (this.$refs?.flagged?.saveAll) {
          await this._withPartialSave(async () => {
            await this.$refs.flagged.saveAll();
          }, "flagged");
        }

        await this._saveFinalSilent();

        this.didBootstrap = false;
        await this.loadEvaluation(false);
        await this.ensureAnswerExists();

        if (typeof this.$refs.runner?.reloadForm === "function") {
          await this.$refs.runner.reloadForm();
        }

        const msg = this.tf("custom_forms.answers_saved", "Respuestas guardadas");
        this.$toast &&
          this.$toast({
            component: ToastificationContent,
            props: { title: msg, icon: "CheckIcon", variant: "success" },
          });

        await this.computeApprovalPermission();
      } catch (_) {
        this.$toast?.error?.(this.tf("risk.save_failed", "No se pudo guardar"));
      } finally {
        this.saving = false;
        this._clearStaleChildrenBusy();
      }
    },

    _wireAutosaveChild(componentInstance) {
      if (!componentInstance || typeof componentInstance.$on !== "function") return;

      const events = [
        "changed",
        "dirty",
        "input",
        "update",
        "update:modelValue",
        "weights-changed",
        "answers-changed",
        "files-changed",
        "followups-changed",
      ];

      events.forEach((eventName) => {
        try {
          componentInstance.$off(eventName, this._boundChildDirty);
        } catch (_) {}

        try {
          const handler =
            this._boundChildDirty ||
            (this._boundChildDirty = (payload) =>
              this.onChildDirty(payload || eventName));
          componentInstance.$on(eventName, handler);
        } catch (_) {}
      });
    },

    _sleep(ms = 250) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    onToc(t) {
      this._tocForm = Array.isArray(t) ? t : [];
      this.refreshToc();
    },
    refreshToc() {
      const merged = [...this._tocForm];
      const addIf = (node) => {
        if (node && node.children && node.children.length) {
          const exists = merged.some((s) => s && s.id === node.id);
          if (!exists) merged.push(node);
        }
      };
      addIf(this._tocThreats);
      addIf(this._tocNona);
      addIf(this._tocFinal);
      this.toc = merged;
    },

    updateThreatsTocFromDom() {
      try {
        const modalEl = document.querySelector(".modal.show");
        if (!modalEl) return;

        const hasThreats = !!modalEl.querySelector(".rfm-aid-sec-amenazas");
        if (!hasThreats) {
          this._tocThreats = null;
        } else {
          const slugify = (s) =>
            String(s || "")
              .toLowerCase()
              .trim()
              .replace(/[^\w\s-]/g, "")
              .replace(/\s+/g, "-")
              .replace(/-+/g, "-");

          const children = [];
          if (modalEl.querySelector(".rfm-aid-sub-amenazas-pesos")) {
            children.push({
              id: "sub-amenazas-pesos",
              label: this.tf("risk.weights_title", "Pesos por catálogo"),
            });
          }

          const threatsComponent = this.$refs?.threats || null;
          if (threatsComponent && Array.isArray(threatsComponent.sectionsOrdered)) {
            const seen = new Set();
            threatsComponent.sectionsOrdered.forEach((section) => {
              const name =
                section && section.catalog_name ? String(section.catalog_name) : "";
              if (!name || seen.has(name)) return;
              const id = "sub-amenazas-" + slugify(name);
              if (modalEl.querySelector(".rfm-aid-" + id)) {
                children.push({ id, label: name });
                seen.add(name);
              }
            });
          }

          if (modalEl.querySelector(".rfm-aid-sub-amenazas-resumen")) {
            children.push({
              id: "sub-amenazas-resumen",
              label: this.tf("risk.global_summary", "Resumen"),
            });
          }
          if (modalEl.querySelector(".rfm-aid-sub-amenazas-resultados")) {
            children.push({
              id: "sub-amenazas-resultados",
              label: this.tf("risk.weighted_results", "Resultados"),
            });
          }

          this._tocThreats = {
            id: "sec-amenazas",
            label: this.tf("risk.threats_title", "Amenazas"),
            children,
          };
        }

        const hasNona = !!modalEl.querySelector(".rfm-aid-sec-nona");
        if (!hasNona) {
          this._tocNona = null;
        } else {
          const nonaChildren = [];
          if (modalEl.querySelector(".rfm-aid-sub-nona-listado")) {
            nonaChildren.push({
              id: "sub-nona-listado",
              label: this.tf("risk.nona_list", "Listado"),
            });
          }
          if (modalEl.querySelector(".rfm-aid-sub-nona-followups")) {
            nonaChildren.push({
              id: "sub-nona-followups",
              label: this.tf("risk.nona_followups", "Seguimiento"),
            });
          }
          this._tocNona = {
            id: "sec-nona",
            label: this.tf("risk.nona_title", "Respuestas NO/NA"),
            children: nonaChildren,
          };
        }

        const hasFinal = !!modalEl.querySelector(".rfm-aid-sec-final");
        this._tocFinal = hasFinal
          ? {
              id: "sec-final",
              label: this.tf("risk.final_section", "Sección Final"),
              children: [
                {
                  id: "sec-final",
                  label: this.tf("risk.final_review", "Revisión & Ejecutivo"),
                },
              ],
            }
          : null;

        this.refreshToc();
      } catch (_) {}
    },

    setupTocObserver() {
      try {
        this.teardownTocObserver();
        const modalEl =
          this.$el ||
          document.querySelector("#risk_form_modal") ||
          document.querySelector(".modal.show");
        if (!modalEl) return;
        this._tocObserver = new MutationObserver(() => {
          this.updateThreatsTocFromDom();
        });
        this._tocObserver.observe(modalEl, { childList: true, subtree: true });
        this.$nextTick(() => {
          this.updateThreatsTocFromDom();
        });
      } catch (_) {}
    },

    teardownTocObserver() {
      if (this._tocObserver) {
        try {
          this._tocObserver.disconnect();
        } catch (_) {}
        this._tocObserver = null;
      }
    },

    scrollToAnchor(id) {
      if (!id) return;
      const safeId = window.CSS && CSS.escape ? CSS.escape(String(id)) : String(id);
      const modalEl =
        document.querySelector("#risk_form_modal") ||
        document.querySelector(".modal.show");
      if (!modalEl) return;

      const container =
        modalEl.querySelector(".form-container") ||
        modalEl.querySelector(".content-grid") ||
        modalEl.querySelector(".rfm-body") ||
        modalEl.querySelector(".modal-body") ||
        modalEl.querySelector(".modal-content") ||
        modalEl;

      const target =
        modalEl.querySelector(`.rfm-anchor.rfm-aid-${safeId}`) ||
        modalEl.querySelector(`[class*="rfm-aid-${safeId}"]`) ||
        modalEl.querySelector(`#${safeId}`) ||
        null;

      if (!container || !target) return;

      this.$nextTick(() => {
        try {
          const cRect = container.getBoundingClientRect();
          const tRect = target.getBoundingClientRect();
          const top = tRect.top - cRect.top + container.scrollTop - 20;
          container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
        } catch (_) {}
      });
    },

    async fetchInherentLevel() {
      try {
        const { data } = await axios.get(
          `/v1/risk_evaluations/${this.riskEvaluationId}/threats/summary`,
          {
            timeout: this.autosaveRequestTimeoutMs,
          }
        );
        const payload = data?.data || data || {};
        const val = Number(payload?.weighted?.inherent ?? payload?.inherent ?? null);
        if (!Number.isFinite(val)) return null;
        let lvl = Math.round(val);
        if (lvl < 1) lvl = 1;
        if (lvl > 5) lvl = 5;
        return lvl;
      } catch {
        return null;
      }
    },

    async computeApprovalPermission() {
      const reviewedResidualRisk = this._extractReviewedResidualRiskValue(
        this.evaluationFinal
      );

      let level = reviewedResidualRisk != null ? Number(reviewedResidualRisk) : null;
      if (!(Number.isFinite(level) && level >= 1 && level <= 5)) {
        level = await this.fetchInherentLevel();
      }

      this.inherentLevel = Number.isFinite(level) ? level : null;
      if (this.inherentLevel == null) {
        this.approvePermNeeded = null;
        return;
      }

      this.approvePermNeeded =
        this.inherentLevel <= 3 ? "approve_low_risk" : "approve_high_risk";
    },
  },
};
</script>

<style>
.rfm-fullscreen {
  padding: 0 !important;
}
.rfm-dialog {
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  margin: 0;
}
.rfm-content {
  min-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  border: 0;
  border-radius: 0;
}
.rfm-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
</style>

<style scoped>
.form-container {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
.rfm-busy-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}
.rfm-busy-box {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
}
.rfm-grid {
  display: grid;
  grid-template-columns: 1.1fr 1.9fr;
  grid-template-areas: "left right";
  gap: 12px;
  align-items: start;
}
.rfm-grid > .header-grid {
  grid-area: left;
  position: sticky;
  top: 0;
  align-self: start;
}
.rfm-grid > .content-grid {
  grid-area: right;
}
@media (max-width: 992px) {
  .rfm-grid {
    grid-template-columns: 1fr;
    grid-template-areas: "right" "left";
  }
  .rfm-grid > .header-grid {
    position: static;
  }
  .index-card {
    max-height: none;
  }
}
.header-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.info-card {
  background: #f9fafb;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 10px 12px;
}
.info-row {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
}
.info-row-associated {
  align-items: flex-start;
}
.info-label {
  font-weight: 600;
  color: #444;
}
.info-value {
  color: #111;
}
.associated-units-list {
  list-style: none;
}
.associated-units-item {
  line-height: 1.35;
}
.history-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  overflow: hidden;
}
.history-header {
  margin-bottom: 8px;
}
.history-table {
  width: 100%;
}
.history-row {
  display: grid;
  column-gap: 8px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed #eee;
}
.history-row:last-child {
  border-bottom: 0;
}
.history-row--head {
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #444;
  padding-top: 0;
  padding-bottom: 8px;
}
.col-state {
  justify-self: start;
}
.col-user {
  justify-self: start;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.col-date {
  justify-self: end;
  white-space: nowrap;
}
.col-note {
  white-space: normal;
  overflow: hidden;
}
.history-badge {
  display: inline-flex;
  align-items: center;
}
.history-date {
  font-size: 12px;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}
.content-grid {
  display: block;
}
.index-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  max-height: calc(100vh - 220px);
  overflow: auto;
}
.toc-list {
  list-style: none;
  margin: 0;
  padding-left: 0;
}
.toc-list > li {
  margin: 4px 0;
}
.toc-link {
  color: #374151;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.toc-link:hover {
  color: #111827;
}
.toc-sub {
  list-style: none;
  margin: 4px 0 0 12px;
  padding-left: 0;
}
.rfm-footer {
  position: relative;
}
.reviewer-center {
  min-width: 0;
}
.reviewer-inline {
  flex: 0 1 560px;
  min-width: 460px;
  max-width: 680px;
}
@media (max-width: 768px) {
  .reviewer-inline {
    flex: 1 1 auto;
    min-width: 0;
  }
}
.reviewer-inline :deep(.vs__dropdown-toggle) {
  min-height: 38px;
}
.reviewer-inline :deep(.vs__dropdown-menu),
.reviewer-inline :deep(.vs__dropdown-option) {
  font-size: 0.8125rem;
}
</style>

<style>
.vs__dropdown-menu {
  z-index: 2055 !important;
}
.select-opens-up .vs__dropdown-menu {
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 4px;
}
</style>

<style>
.dark-layout .rfm-busy-overlay {
  background: rgba(14, 30, 45, 0.8) !important;
}
.dark-layout .rfm-busy-box {
  background: var(--third-dark) !important;
  border-color: var(--second-dark) !important;
}
.dark-layout .history-row {
  border-bottom-color: rgba(255, 255, 255, 0.1) !important;
}
.dark-layout .history-row--head {
  border-bottom-color: rgba(255, 255, 255, 0.15) !important;
}
.dark-layout .toc-link {
  color: #d0d2d6 !important;
}
.dark-layout .toc-link:hover {
  color: #f0f0f0 !important;
}

.reload-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 50%;
  border: none !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.reload-icon {
  font-size: 14px;
  line-height: 1;
  display: inline-block;
  margin-top: -1px;
}
</style>
